import * as React from 'react';
import { Maximize2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type WindowPos = { x: number; y: number; w: number; h: number };

export const WINDOWED_POS_KEY = 'lokma:windowed-pos:v1';
export const WINDOWED_MIN_W = 320;
export const WINDOWED_MIN_H = 220;

/** Measured canvas box (viewport coords + size) reported by the canvas. */
export type CanvasBox = { left: number; top: number; w: number; h: number };

const WIN_M = 8;

/** Fill the whole canvas with an 8px margin (REQ-096 maximize). */
export function fillWindowPos(box: { w: number; h: number }): WindowPos {
  return {
    x: WIN_M,
    y: WIN_M,
    w: Math.max(WINDOWED_MIN_W, box.w - WIN_M * 2),
    h: Math.max(WINDOWED_MIN_H, box.h - WIN_M * 2),
  };
}

/** Clamp a window inside the canvas (REQ-096 containment). No-op until measured. */
export function clampWindowPos(p: WindowPos, box: { w: number; h: number }): WindowPos {
  if (!box || box.w <= 0 || box.h <= 0) return p;
  const w = Math.max(WINDOWED_MIN_W, Math.min(p.w, box.w - WIN_M * 2));
  const h = Math.max(WINDOWED_MIN_H, Math.min(p.h, box.h - WIN_M * 2));
  return {
    // REQ-151: both axes keep the WHOLE window inside. The y rule used to
    // reserve only 40px (an imagined title bar), so dragging a window down
    // parked it below the canvas and `overflow-hidden` clipped everything but
    // the title — the user reads that as "it sinks into the bottom".
    x: Math.max(0, Math.min(Math.round(p.x), Math.max(0, box.w - WIN_M - w))),
    y: Math.max(0, Math.min(Math.round(p.y), Math.max(0, box.h - WIN_M - h))),
    w: Math.round(w),
    h: Math.round(h),
  };
}

// ─── REQ-152: floating-window stacking (Windows-style focus) ────────────────
// `order` lists window ids back-to-front: the LAST entry is the focused one.
// It is persisted so a reload keeps the same stacking.
export const WINDOWED_ORDER_KEY = 'lokma:windowed-order:v1';

/** Keep the stored order, drop ids that are gone, put new ids in front. */
export function orderWindows(order: string[], ids: string[]): string[] {
  const kept = order.filter((id) => ids.includes(id));
  const seen = new Set(kept);
  const added = ids.filter((id) => !seen.has(id));
  return [...kept, ...added];
}

/** Move `id` to the front of the stack (no-op when it already is). */
export function raiseWindow(order: string[], id: string): string[] {
  if (!id || order[order.length - 1] === id) return order;
  return [...order.filter((x) => x !== id), id];
}

/** z-index for a window: 1-based position in the stack order. */
export function windowZ(order: string[], id: string): number {
  const i = order.indexOf(id);
  return i < 0 ? 0 : i + 1;
}

/** Validated read of the persisted stacking order. */
export function parseWindowOrder(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

export type SnapEdge = 'left' | 'right' | 'top';

/** Windows-style snap geometry (REQ-096): halves + full. */
export function snapWindowPos(box: { w: number; h: number }, edge: SnapEdge): WindowPos {
  const full = fillWindowPos(box);
  if (edge === 'top') return full;
  const w = Math.max(WINDOWED_MIN_W, Math.floor((box.w - WIN_M * 3) / 2));
  return edge === 'left'
    ? { x: WIN_M, y: WIN_M, w, h: full.h }
    : { x: WIN_M * 2 + w, y: WIN_M, w: Math.max(WINDOWED_MIN_W, box.w - WIN_M * 3 - w), h: full.h };
}

/** Which edge (if any) a pointer release near the canvas border snaps to. */
export function snapEdgeForPoint(
  box: CanvasBox,
  clientX: number,
  clientY: number,
  tol = 20,
): SnapEdge | null {
  if (!box || box.w <= 0 || box.h <= 0) return null;
  const lx = clientX - box.left;
  const ly = clientY - box.top;
  if (ly >= -tol && ly <= tol) return 'top';
  if (lx >= -tol && lx <= tol) return 'left';
  if (lx >= box.w - tol && lx <= box.w + tol) return 'right';
  return null;
}

export function parseWindowedPos(raw: unknown): Record<string, WindowPos> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, WindowPos> = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue;
    const p = v as Record<string, unknown>;
    const nums = [p['x'], p['y'], p['w'], p['h']];
    if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n))) continue;
    out[id] = {
      x: Math.max(0, Math.round(p['x'] as number)),
      y: Math.max(0, Math.round(p['y'] as number)),
      w: Math.max(WINDOWED_MIN_W, Math.round(p['w'] as number)),
      h: Math.max(WINDOWED_MIN_H, Math.round(p['h'] as number)),
    };
  }
  return out;
}

// WindowedCanvas: concept panes/WindowedCanvas.tsx port. Floating,
// draggable, resizable windows over the tiling canvas. Glyph buttons
// (concept box/x) are lucide icons per the no-symbol-glyph rule.
export function WindowedCanvas({
  panes,
  pos,
  renderPane,
  onDragStart,
  onResize,
  onMaximize,
  onClose,
  onBox,
  zOf,
  onFocus,
}: {
  panes: { id: string; title: string }[];
  pos: Record<string, WindowPos>;
  renderPane: (id: string) => React.ReactNode;
  onDragStart: (id: string, x: number, y: number) => void;
  onResize: (id: string, next: WindowPos) => void;
  onMaximize: (id: string) => void;
  onClose: (id: string) => void;
  /** Canvas box reporter (REQ-096): measured rect for clamp/snap/maximize. */
  onBox: (box: CanvasBox) => void;
  /** REQ-152: stacking z-index per window (1 = back, higher = front). */
  zOf: (id: string) => number;
  /** REQ-152: any pointer/focus landing in a window raises it to the front. */
  onFocus: (id: string) => void;
}) {
  const boxEl = React.useRef<HTMLDivElement | null>(null);
  const onBoxRef = React.useRef(onBox);
  onBoxRef.current = onBox;
  React.useEffect(() => {
    const el = boxEl.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const report = () => {
      const r = el.getBoundingClientRect();
      onBoxRef.current({
        left: Math.round(r.left),
        top: Math.round(r.top),
        w: Math.round(r.width),
        h: Math.round(r.height),
      });
    };
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boxEl} className="relative min-h-0 flex-1 overflow-hidden rounded border bg-muted/20">
      {panes.length === 0 ? (
        <div className="flex h-full items-center justify-center p-6 text-center text-xs text-muted-foreground">
          No floating windows. Pop a pane out from its strip button, then drag it here.
        </div>
      ) : null}
      {/* REQ-014: window frames use a solid card surface. `bg-background`
          has no rule in the Tailwind v4 bundle (@theme defines no
          --color-background, so the class compiles to nothing and the
          window renders as a transparent ghost). `bg-white` is solid in
          light and maps to #1E1E21 via the `.dark .bg-white` override
          in index.css. Tiling split panes are untouched (already opaque). */}
      {panes.map((pane, i) => {
        const p = pos[pane.id] ?? { x: 24 + i * 28, y: 24 + i * 28, w: 560, h: 420 };
        const z = zOf(pane.id);
        // REQ-152: the focused window (highest z) is visibly in front —
        // stronger shadow + terracotta-tinted frame; the rest recede.
        const front = z > 0 && z === Math.max(...panes.map((x) => zOf(x.id)));
        return (
          <div
            key={pane.id}
            data-window-id={pane.id}
            data-window-z={z}
            className={
              'absolute flex flex-col overflow-hidden rounded-lg bg-white ' +
              (front
                ? 'border border-[#E8B99E] shadow-2xl ring-1 ring-[#C96442]/30'
                : 'border shadow-md')
            }
            style={{ left: p.x, top: p.y, width: p.w, height: p.h, zIndex: z }}
            onPointerDownCapture={() => onFocus(pane.id)}
            onFocusCapture={() => onFocus(pane.id)}
          >
            <div
              className="flex h-8 shrink-0 cursor-move items-center gap-1 border-b bg-muted/60 px-2"
              onPointerDown={(e) => onDragStart(pane.id, e.clientX, e.clientY)}
            >
              <span className="min-w-0 flex-1 truncate text-xs font-medium">{pane.title}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Maximize window" onClick={() => onMaximize(pane.id)} aria-label="Maximize window">
                <Maximize2 className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Close window" onClick={() => onClose(pane.id)} aria-label="Close window">
                <X className="h-3 w-3" />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">{renderPane(pane.id)}</div>
            <WindowResizeHandles pos={p} onResize={(next) => onResize(pane.id, next)} />
          </div>
        );
      })}
    </div>
  );
}

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const DIR_CURSOR: Record<ResizeDir, string> = {
  'n': 'cursor-ns-resize',
  's': 'cursor-ns-resize',
  'e': 'cursor-ew-resize',
  'w': 'cursor-ew-resize',
  'ne': 'cursor-nesw-resize',
  'sw': 'cursor-nesw-resize',
  'nw': 'cursor-nwse-resize',
  'se': 'cursor-nwse-resize',
};

// REQ-042: resize from all four edges plus all four corners. North/west
// handles move the origin as well as the size; every direction clamps to
// the shared minimums so a window can never collapse to zero.
function WindowResizeHandles({ pos, onResize }: { pos: WindowPos; onResize: (next: WindowPos) => void }) {
  const start = (e: React.PointerEvent, dir: ResizeDir) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const sx = pos.x;
    const sy = pos.y;
    const sw = pos.w;
    const sh = pos.h;
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let x = sx;
      let y = sy;
      let w = sw;
      let h = sh;
      if (dir.includes('e')) w = Math.max(WINDOWED_MIN_W, sw + dx);
      if (dir.includes('s')) h = Math.max(WINDOWED_MIN_H, sh + dy);
      if (dir.includes('w')) {
        const nw = Math.max(WINDOWED_MIN_W, sw - dx);
        x = Math.max(0, sx + (sw - nw));
        w = nw;
      }
      if (dir.includes('n')) {
        const nh = Math.max(WINDOWED_MIN_H, sh - dy);
        y = Math.max(0, sy + (sh - nh));
        h = nh;
      }
      onResize({ x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 32 : 8;
    let w = pos.w;
    let h = pos.h;
    if (e.key === 'ArrowRight') w += step;
    else if (e.key === 'ArrowLeft') w -= step;
    else if (e.key === 'ArrowDown') h += step;
    else if (e.key === 'ArrowUp') h -= step;
    else return;
    e.preventDefault();
    onResize({ 'x': pos.x, 'y': pos.y, 'w': Math.max(WINDOWED_MIN_W, w), 'h': Math.max(WINDOWED_MIN_H, h) });
  };

  const edge = 'absolute z-10 touch-none hover:bg-foreground/10';
  return (
    <>
      <div role="separator" aria-orientation="horizontal" aria-label="Resize window from top edge" title="Resize window" className={edge + ' left-3 right-3 top-0 h-1.5 ' + DIR_CURSOR['n']} onPointerDown={(e) => start(e, 'n')} />
      <div role="separator" aria-orientation="horizontal" aria-label="Resize window from bottom edge" title="Resize window" className={edge + ' bottom-0 left-3 right-3 h-1.5 ' + DIR_CURSOR['s']} onPointerDown={(e) => start(e, 's')} />
      <div role="separator" aria-orientation="vertical" aria-label="Resize window from left edge" title="Resize window" className={edge + ' bottom-3 left-0 top-3 w-1.5 ' + DIR_CURSOR['w']} onPointerDown={(e) => start(e, 'w')} />
      <div role="separator" aria-orientation="vertical" aria-label="Resize window from right edge" title="Resize window" className={edge + ' bottom-3 right-0 top-3 w-1.5 ' + DIR_CURSOR['e']} onPointerDown={(e) => start(e, 'e')} />
      <div role="separator" aria-label="Resize window from top left corner" title="Resize window" className={edge + ' left-0 top-0 h-3 w-3 ' + DIR_CURSOR['nw']} onPointerDown={(e) => start(e, 'nw')} />
      <div role="separator" aria-label="Resize window from top right corner" title="Resize window" className={edge + ' right-0 top-0 h-3 w-3 ' + DIR_CURSOR['ne']} onPointerDown={(e) => start(e, 'ne')} />
      <div role="separator" aria-label="Resize window from bottom left corner" title="Resize window" className={edge + ' bottom-0 left-0 h-3 w-3 ' + DIR_CURSOR['sw']} onPointerDown={(e) => start(e, 'sw')} />
      <div
        role="separator"
        aria-label="Resize window from bottom right corner. Arrow keys resize, shift makes bigger steps."
        title="Resize window"
        tabIndex={0}
        onKeyDown={onKey}
        className={edge + ' bottom-0 right-0 h-4 w-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ' + DIR_CURSOR['se']}
        onPointerDown={(e) => start(e, 'se')}
      />
    </>
  );
}
